import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Collapse, Divider, Skeleton, Typography } from '@mui/material';
import BackButton from '@sharedCR/common/BackButton';
import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import PopoverCDS from '@sharedCR/coreDesignSystem/PopoverCDS';
import PopTipCDS from '@sharedCR/coreDesignSystem/PopTipCDS';
import { PipelinesPaths } from 'app/configs/routesConfig/pipelines';
import PrepareDisclosuresWizard from 'app/pages/loanApplication/PrepareDisclosuresWizard';
import { FileHistoryIcon, SearchIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import clsx from 'clsx';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { IconSize } from 'constants/coreDesignSystem';
import { FeatureFlag } from 'constants/featureFlags';
import { useBreakpoints } from 'hooks/useBreakpoints';
import useContentWidth from 'hooks/useContentWidth';
import useLoanId from 'hooks/useLoanId';
import usePopover from 'hooks/usePopover';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';
import { checkIsPurchaseFlow } from 'utils/loanApplication';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import { showMessageAction } from 'store/global/actions';
import { createLoanCloneAction, sendNoiAction } from 'store/loanApplications/actions';
import {
  selectCommonInfoLoanApplication,
  selectIsApplicationNotCompleted,
  selectIsLoanCanceled,
  selectIsLoanChangesNotSaved,
  selectLoanHeaderApplicationData,
  selectSecondaryLoanHeaderView,
} from 'store/loanApplications/selectors';

import HeaderGrid from '../HeaderGrid';
import { flattenHeaderColumns } from '../HeaderGrid/HeaderGrid.utils';
import { LoanHeadersTestIds } from '../LoanHeaders.testIds';

import ActionButton from './ActionButton';
import { dividerStyles } from './ActionButton/ActionButton.styles';
import DisabledReasonTooltip from './DisabledReasonTooltip';
import {
  LoanActionsControlsGroupedSlot,
  LoanActionsControlsProvider,
  LoanActionsControlsTopLevelSlot,
  PrepareDisclosuresCta,
} from './LoanActionsControls';
import {
  COLUMN_GAP,
  COPY_LOAN_DISABLED_REASON,
  MIN_COLUMN_WIDTH,
} from './LoanInfoHeader.constants';
import {
  contentTitleStyles,
  contentWrapperStyles,
  controlsWrapper,
  drawerStyles,
  groupedActionsWrapper,
  headerGridWithMoreButtonStyles,
  moreButtonWrapperStyles,
  overflowModalContentStyles,
  searchButtonStyles,
  searchShortcutBadgeStyles,
  toolButtonStyles,
  toolsButtonsWrapper,
  tooltipWrapperStyles,
  wrapperStyles,
} from './LoanInfoHeader.styles';
import LoanInfoHeaderItem from './LoanInfoHeaderItem';
import LogHistoryDrawer from './LogHistoryDrawer';
import OpenDocumentsPaneButton from './OpenDocumentsPaneButton';
import PendingChangesAuthorsNotice from './PendingChangesAuthorsNotice';
import SavePendingChangesButton from './SavePendingChangesButton';

const LoanInfoHeader = ({
  isExpanded,
  isModifierHeld,
  onOpenSearch,
  shortcutPrefix,
  setIsLoading,
}) => {
  const loanId = useLoanId();
  const { applicationId } = useParams();
  const { state } = useLocation();

  const dispatch = useDispatch();

  const [isSendNoiLoading, setIsSendNoiLoading] = useState(false);
  const [isCopyLoanLoading, setIsCopyLoanLoading] = useState(false);
  const [isLogHistoryOpen, setIsLogHistoryOpen] = useState(false);

  const {
    anchorEl: overflowAnchorEl,
    onSetAnchorElement: onOpenOverflowPopover,
    onClearAnchorElement: onCloseOverflowPopover,
  } = usePopover();

  const pipelinesHrefRef = useRef(state?.pipelinesHref ?? PipelinesPaths.loanPipelines._path);

  const openStateClassname = clsx({ open: isExpanded });

  const secondaryLoanHeaderView = useSelector(selectSecondaryLoanHeaderView);

  const { isMobile, isTablet } = useBreakpoints();
  const { setRef: contentWidthRef, contentWidth } = useContentWidth();

  const maxVisibleColumns = useMemo(() => {
    if (isMobile) {
      return 1;
    }

    if (isTablet) {
      return 2;
    }

    if (!contentWidth) {
      return Infinity;
    }

    return Math.max(1, Math.floor((contentWidth + COLUMN_GAP) / (MIN_COLUMN_WIDTH + COLUMN_GAP)));
  }, [isMobile, isTablet, contentWidth]);

  const totalColumns = secondaryLoanHeaderView?.length ?? 0;
  const hasOverflow = totalColumns > maxVisibleColumns;

  const visibleColumns = useMemo(() => {
    if (!secondaryLoanHeaderView?.length) {
      return [];
    }

    if (!hasOverflow) {
      return secondaryLoanHeaderView;
    }

    return secondaryLoanHeaderView.slice(0, maxVisibleColumns);
  }, [secondaryLoanHeaderView, hasOverflow, maxVisibleColumns]);

  const popoverAnchorOrigin = useMemo(() => ({ vertical: 'bottom', horizontal: 'center' }), []);
  const popoverTransformOrigin = useMemo(() => ({ vertical: 'top', horizontal: 'center' }), []);

  const isNoiSendAvailable = useFeatureToggle(FeatureFlag.EnableNoiSend);
  const isSavePendingChangesLoanActionEnabled = useFeatureToggle(
    FeatureFlag.EnableSavePendingChangesLoanAction,
  );

  const loanHeaderApplicationData = useSelector(selectLoanHeaderApplicationData);
  const model = useMemo(() => loanHeaderApplicationData || {}, [loanHeaderApplicationData]);

  const renderSecondaryHeaderItem = useCallback(
    item => (
      <LoanInfoHeaderItem applicationId={applicationId} key={item.id} model={model} {...item} />
    ),
    [applicationId, model],
  );
  const { loanPurpose } = useSelector(selectCommonInfoLoanApplication);
  const isApplicationNotCompleted = useSelector(selectIsApplicationNotCompleted);
  const isLoanCanceled = useSelector(selectIsLoanCanceled);
  const isLoanChangesNotSaved = useSelector(selectIsLoanChangesNotSaved);

  const isViewLogAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_VIEW_LOG),
  );

  const isCopyLoanAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_COPY_LOAN),
  );

  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const isPurchaseFlow = checkIsPurchaseFlow(loanPurpose);

  const isCopyLoanButtonDisplayed = isCopyLoanAllowed && isPurchaseFlow && !isLoanCanceled;

  const onOpenLogHistory = useCallback(() => {
    setIsLogHistoryOpen(true);
  }, []);

  const onCloseLogHistory = useCallback(() => {
    setIsLogHistoryOpen(false);
  }, []);

  const onNoiSend = useCallback(() => {
    setIsLoading(true);
    setIsSendNoiLoading(true);

    dispatch(sendNoiAction({ applicationId }))
      .catch(Error)
      .finally(() => {
        setIsLoading(false);
        setIsSendNoiLoading(false);
      });
  }, [dispatch, applicationId, setIsLoading]);

  const onCopyLoan = useCallback(() => {
    setIsLoading(true);
    setIsCopyLoanLoading(true);

    dispatch(createLoanCloneAction({ loanId }))
      .then(() =>
        dispatch(
          showMessageAction({
            message: `Executed successfully`,
            variant: 'success',
          }),
        ),
      )
      .catch(Error)
      .finally(() => {
        setIsLoading(false);
        setIsCopyLoanLoading(false);
      });
  }, [dispatch, loanId, setIsLoading]);

  return (
    <>
      <Box
        className={openStateClassname}
        data-testid={LoanHeadersTestIds.ASSOCIATE_REPORT_FORM_LOAN_INFO_HEADER}
        sx={wrapperStyles}
      >
        <LoanActionsControlsProvider>
          <Box sx={controlsWrapper}>
            <Box sx={toolsButtonsWrapper}>
              <BackButton to={pipelinesHrefRef.current} />

              <Divider orientation="vertical" sx={dividerStyles} />

              {isViewLogAllowed && (
                <ActionButton
                  withDividerAfter
                  customStyles={toolButtonStyles}
                  onClick={onOpenLogHistory}
                >
                  <FileHistoryIcon size={IconSize[24]} />
                </ActionButton>
              )}
              {isLoanChangesNotSaved && !isSavePendingChangesLoanActionEnabled && (
                <>
                  <SavePendingChangesButton />
                  <Divider orientation="vertical" sx={dividerStyles} />
                </>
              )}

              <ActionButton customStyles={searchButtonStyles} onClick={onOpenSearch}>
                <SearchIcon size={IconSize[16]} />
                {isModifierHeld && shortcutPrefix && (
                  <Typography component="span" sx={searchShortcutBadgeStyles} variant="regular12">
                    {`${shortcutPrefix}K`}
                  </Typography>
                )}
              </ActionButton>
              <OpenDocumentsPaneButton />
              <LoanActionsControlsTopLevelSlot />
              <PrepareDisclosuresCta />
              {isCopyLoanButtonDisplayed && (
                <PopTipCDS
                  title={
                    isApplicationNotCompleted ? (
                      <DisabledReasonTooltip title={COPY_LOAN_DISABLED_REASON} />
                    ) : (
                      ''
                    )
                  }
                >
                  <Box component="span" sx={tooltipWrapperStyles}>
                    <ActionButton
                      dataTestId={LoanHeadersTestIds.LOAN_INFO_HEADER_COPY_LOAN_BUTTON}
                      disabled={isCopyLoanLoading || isApplicationNotCompleted}
                      onClick={onCopyLoan}
                    >
                      Copy Loan
                    </ActionButton>
                  </Box>
                </PopTipCDS>
              )}
              {isNoiSendAvailable && (
                <ActionButton disabled={isSendNoiLoading || !applicationId} onClick={onNoiSend}>
                  NOI Send
                </ActionButton>
              )}
            </Box>

            <Box sx={groupedActionsWrapper}>
              <LoanActionsControlsGroupedSlot />
            </Box>
          </Box>
        </LoanActionsControlsProvider>

        <PendingChangesAuthorsNotice />

        <Box className={openStateClassname} sx={drawerStyles}>
          <Collapse in={isExpanded} timeout={100}>
            {_.isNil(secondaryLoanHeaderView) && (
              <Box className="grid grid-rows-4 gap-10 w-full py-16 px-32">
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="100%" />
              </Box>
            )}

            {!!secondaryLoanHeaderView?.length && (
              <Box
                className={openStateClassname}
                sx={contentWrapperStyles}
                onMouseLeave={onResetTooltipState}
                onMouseOver={onMouseOver}
              >
                <TruncatedProvider />

                <Typography sx={contentTitleStyles} variant="regular14">
                  Detailed Summary
                </Typography>
                <Box ref={contentWidthRef} sx={headerGridWithMoreButtonStyles}>
                  <HeaderGrid columns={visibleColumns} renderItem={renderSecondaryHeaderItem} />
                  {hasOverflow && (
                    <Box sx={moreButtonWrapperStyles}>
                      <MoreButtonCDS
                        isSelected={!!overflowAnchorEl}
                        onClick={onOpenOverflowPopover}
                      />
                    </Box>
                  )}
                </Box>

                <PopoverCDS
                  anchorEl={overflowAnchorEl}
                  anchorOrigin={popoverAnchorOrigin}
                  open={!!overflowAnchorEl}
                  title="All Summary Fields"
                  transformOrigin={popoverTransformOrigin}
                  onClose={onCloseOverflowPopover}
                >
                  <Box sx={overflowModalContentStyles}>
                    {flattenHeaderColumns(secondaryLoanHeaderView).map(item =>
                      renderSecondaryHeaderItem(item),
                    )}
                  </Box>
                </PopoverCDS>
              </Box>
            )}
          </Collapse>
        </Box>
      </Box>
      <LogHistoryDrawer isOpen={isLogHistoryOpen} onClose={onCloseLogHistory} />
      <PrepareDisclosuresWizard />
    </>
  );
};

LoanInfoHeader.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  onOpenSearch: PropTypes.func.isRequired,
  isModifierHeld: PropTypes.bool,
  shortcutPrefix: PropTypes.string,
  ...WithLoadingPropTypes,
};

export default withLoading(LoanInfoHeader);
